import { SvgIcon, TextInput } from '@/components/ui';
import { createStyles, useScaling } from '@/hooks';
import { useDebounce } from '@/hooks/use-debounce';
import { colors } from '@/theme/colors';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard, TextInput as RNTextInput, TouchableOpacity, View } from 'react-native';
import { KeyboardController } from 'react-native-keyboard-controller';

export type ListPlayersSearchBarProps = {
  placeholder: string;
  onChangeText?: (text: string) => void;
  onClear?: () => void;
  value?: string;
};

export const SearchBar = ({
  placeholder,
  onChangeText,
  onClear,
  value,
}: ListPlayersSearchBarProps) => {
  const styles = useStyles();
  const { scale } = useScaling();

  const [innerValue, setInnerValue] = useState(value);
  const inputRef = useRef<RNTextInput>(null);

  // See note in `components/template/player-list/v2/search-bar.tsx`: `KeyboardController.dismiss()`
  // alone fails on Samsung DeX / freeform Android. Blurring the input is the universal fallback.
  const dismissKeyboard = useCallback(() => {
    inputRef.current?.blur();
    Keyboard.dismiss();
    KeyboardController.dismiss();
  }, []);

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  const debouncedOnChangeText = useDebounce((newValue: string) => {
    onChangeText?.(newValue);
    dismissKeyboard();
  }, 1500);
  const debouncedOnClear = useDebounce(
    () => {
      onClear?.();
      dismissKeyboard();
    },
    1000,
    { leading: true },
  );

  const handleChangeText = (text: string) => {
    setInnerValue(text);
    debouncedOnChangeText(text);
  };

  const handleClear = () => {
    debouncedOnChangeText.cancel();
    setInnerValue('');
    debouncedOnClear();
  };

  return (
    <TextInput value={innerValue} onChangeText={handleChangeText} status="default">
      <TextInput.Content style={styles.content}>
        <TextInput.Icon position="left" name="IcSearch" size={scale(20)} color={colors['Blue 2']} />
        <TextInput.Field
          ref={inputRef}
          placeholder={placeholder}
          keyboardType="default"
          maxLength={100}
          autoCapitalize="none"
          autoCorrect={false}
        />
        {innerValue && innerValue.length > 0 ? (
          <TouchableOpacity onPress={handleClear} activeOpacity={0.8} style={styles.clearButton}>
            <SvgIcon name="IcClose" size={scale(12)} color={colors['5']} />
          </TouchableOpacity>
        ) : (
          <View style={styles.clearButtonPlaceholder} />
        )}
      </TextInput.Content>
    </TextInput>
  );
};

const useStyles = createStyles(({ scale }) => ({
  content: {
    backgroundColor: colors['0'],
    borderColor: 'transparent',
    borderWidth: 0,
    height: scale(42),
  },
  clearButton: {
    width: scale(24),
    height: scale(24),
    justifyContent: 'center',
    alignItems: 'center',
  },
  clearButtonPlaceholder: {
    width: scale(24),
    height: scale(24),
  },
}));
